import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useLayoutEffect,
} from "react";
import style from "css/hotdesking.module.css";
import { ReactComponent as ZoomInIcon } from "asset/hotdesking/zoomin.svg";
import { ReactComponent as ZoomOutIcon } from "asset/hotdesking/zoomout.svg";
import { ReactComponent as CalendarIcon } from "asset/hotdesking/calendar.svg";
import { ReactComponent as TimeIcon } from "asset/hotdesking/time.svg";
import pinIcon from "asset/hotdesking/pin.svg";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { UserOutlined } from "@ant-design/icons";
import { Avatar } from "antd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

dayjs.locale("ko");

const spotSize = 15;
const type = {
  AVAILABLE: {
    value: "AVAILABLE",
    // label: t("HOTDESKING.SEAT_GUIDE.RESERVED"),
    color: "#f16569",
    hover: "#eb1c22",
    blur: "#f16569cc",
  },
  IN_USE: {
    value: "IN_USE",
    // label: t("HOTDESKING.SEAT_GUIDE.IN_USE"),
    color: "#fea85e",
    hover: "#fa7807",
    blur: "#fea85ecc",
  },
  NOT_USE: {
    value: "NOT_USE",
    // label: t("HOTDESKING.SEAT_GUIDE.NOT_USE"),
    color: "#5ee2c3",
    hover: "#00ebb4",
    blur: "#5ee2c3cc",
  },
  NOT_AVAILABLE: {
    value: "NOT_AVAILABLE",
    // label: t("HOTDESKING.SEAT_GUIDE.NOT_AVAILABLE"),
    color: "#454545",
    hover: "#454545",
    blur: "#454545cc",
  },
};

const PlanCanvas = ({
  bookList,
  resourceList,
  bookInfo,
  isFind,
  setIsFind,
  isResourceFind,
  setIsResourceFind,
  location,
  refresh,
  setRefresh,
  onBook,
}) => {
  const userPayload = useSelector((state) => state.user);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [init, setInit] = useState(true);
  const [remove, setRemove] = useState(false);
  const [size, setSize] = useState({
    width: 0,
    height: 0,
  });
  const [baseScale, setBaseScale] = useState(1);
  const [scale, setScale] = useState(1);
  const [image, setImage] = useState(null);
  const [pin, setPin] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenResource, setIsOpenResource] = useState(false);
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  const [book, setBook] = useState(null);
  const [resource, setResource] = useState(null);
  const [hoveredResource, setHoverResource] = useState(null);
  const [isHover, setIsHover] = useState(false);
  const [isDraw, setIsDraw] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = pinIcon;
    img.onload = () => setPin(img);
  }, []);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (!image) return;

    canvas.width = size.width;
    canvas.height = size.height;

    // 배경 설정
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 이미지 중심을 기준으로 이동 및 확대
    ctx.save();
    ctx.translate(canvas.width / 2 + offset.x, canvas.height / 2 + offset.y);
    ctx.scale(scale, scale);
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0, image.width, image.height);
    ctx.restore();

    resourceList.forEach((resource) => {
      ctx.beginPath();
      const adjustedX =
        canvas.width / 2 + offset.x + resource.coordinateX * scale;
      const adjustedY =
        canvas.height / 2 + offset.y + resource.coordinateY * scale;
      ctx.arc(adjustedX, adjustedY, spotSize * scale, 0, Math.PI * 2);

      let color = type.NOT_USE.color;
      let hover = type.NOT_USE.hover;
      let blur = type.NOT_USE.blur;
      let isBook = false;
      bookList.forEach((book) => {
        if (book.resource.id === resource.id) {
          switch (book.status) {
            case type.AVAILABLE.value:
              if (userPayload.id === book.member.id) isBook = true;
              color = type.AVAILABLE.color;
              hover = type.AVAILABLE.hover;
              blur = type.AVAILABLE.blur;
              break;
            case type.IN_USE.value:
              if (userPayload.id === book.member.id) isBook = true;
              color = type.IN_USE.color;
              hover = type.IN_USE.hover;
              blur = type.IN_USE.blur;
              break;
            case type.NOT_AVAILABLE.value:
              color = type.NOT_AVAILABLE.color;
              hover = type.NOT_AVAILABLE.hover;
              blur = type.NOT_AVAILABLE.blur;
              break;
            default:
              return;
          }
        }
      });

      if (hoveredResource && hoveredResource.id === resource.id) {
        ctx.shadowColor = blur;
        ctx.shadowBlur = 10;
        ctx.fillStyle = hover;
      } else {
        ctx.fillStyle = color;
        ctx.shadowBlur = 0;
      }

      ctx.fill();

      if (isBook) {
        const iconSize = spotSize * scale * 1.2;
        ctx.drawImage(
          pin,
          adjustedX - iconSize / 2,
          adjustedY - iconSize / 2,
          iconSize,
          iconSize
        );
      }
    });
  }, [
    bookList,
    hoveredResource,
    image,
    offset.x,
    offset.y,
    pin,
    resourceList,
    scale,
    size,
    userPayload.id,
  ]);

  const removeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.style.cursor = "default";

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = size.width;
    canvas.height = size.height;

    // 배경 설정
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [size.height, size.width]);

  const updateCanvas = useCallback(() => {
    const { clientWidth, clientHeight } = containerRef.current;
    setSize({ width: clientWidth, height: clientHeight - 4 });

    if (!location) {
      setImage(null);
      setRemove(true);
      return;
    }

    const img = new Image();
    // img.src = process.env.REACT_APP_FILE_URL + location.source;
    img.src = location.source;
    img.onload = () => {
      setImage(img);
      const scaleX = clientWidth / img.width;
      const scaleY = clientHeight / img.height;
      const minScale = Math.min(scaleX, scaleY);
      setBaseScale(minScale);
      setScale(minScale);
      setOffset({ x: 0, y: 0 });
      setIsDraw(true);
    };
  }, [location]);

  useMemo(() => {
    if (containerRef.current && canvasRef.current) {
      const menuEl = document.getElementById("app_menu");
      const resizeObserver = new ResizeObserver(() => {
        if (location != null) setRefresh(true);
      });
      resizeObserver.observe(menuEl);
    }
  }, [location, setRefresh]);

  const onCloseModal = useCallback(() => {
    setBook(null);
    setResource(null);
    setPointer({ x: 0, y: 0 });
    setIsOpenModal(false);
  }, []);

  const onOpenBookModal = useCallback((book, resource, x, y) => {
    setBook(book);
    setResource(resource);
    setPointer({ x: x, y: y });
    setIsOpenModal(true);
  }, []);

  const onCloseResource = useCallback(() => {
    setResource(null);
    setPointer({ x: 0, y: 0 });
    setIsOpenResource(false);
  }, []);

  const onOpenResource = useCallback((resource, x, y) => {
    setResource(resource);
    setPointer({ x: x, y: y });
    setIsOpenResource(true);
  }, []);

  useEffect(() => {
    if (refresh) {
      updateCanvas();
      setRefresh(false);
    }
  }, [refresh, setRefresh, updateCanvas]);

  useEffect(() => {
    if (init && location != null) {
      updateCanvas();
      setInit(false);
    }
  }, [init, location, setRefresh, updateCanvas]);

  useEffect(() => {
    if (image && !init) {
      drawCanvas();
      window.addEventListener("resize", updateCanvas);
      return () => window.removeEventListener("resize", updateCanvas);
    }
  }, [drawCanvas, image, init, updateCanvas]);

  useLayoutEffect(() => {
    if (remove && image === null) {
      removeCanvas();
      setRemove(false);
    }
  }, [removeCanvas, image, remove]);

  useLayoutEffect(() => {
    if (isFind && isDraw) {
      const rect = containerRef.current.getBoundingClientRect();
      const adjustedX =
        rect.width / 2 + offset.x + bookInfo.resource.coordinateX * scale;
      const adjustedY =
        rect.height / 2 + offset.y + bookInfo.resource.coordinateY * scale;

      onOpenBookModal(
        bookInfo,
        bookInfo.resource,
        adjustedX - spotSize / 2,
        adjustedY - spotSize / 2
      );
      setIsFind(false);
      setIsDraw(false);
    }
  }, [
    bookInfo,
    isDraw,
    isFind,
    offset.x,
    offset.y,
    onOpenBookModal,
    scale,
    setIsFind,
  ]);

  useLayoutEffect(() => {
    if (isResourceFind && isDraw) {
      const rect = containerRef.current.getBoundingClientRect();
      const adjustedX =
        rect.width / 2 + offset.x + bookInfo.resource.coordinateX * scale;
      const adjustedY =
        rect.height / 2 + offset.y + bookInfo.resource.coordinateY * scale;

      onOpenResource(
        bookInfo,
        adjustedX - spotSize / 2,
        adjustedY - spotSize / 2
      );
      setIsResourceFind(false);
      setIsDraw(false);
    }
  }, [
    bookInfo,
    isDraw,
    isResourceFind,
    offset.x,
    offset.y,
    onOpenResource,
    scale,
    setIsResourceFind,
  ]);

  // 확대/축소 핸들러
  const handleZoom = (factor) => {
    setScale((prev) =>
      Math.min(Math.max(prev * factor, baseScale * 0.5), baseScale * 2)
    );
  };

  // 마우스 드래그 이동 핸들러
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });

    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!image) return;
    if (isHover) return;

    canvas.style.cursor = "grabbing";
  };

  const handleMouseMove = (e) => {
    if (!isDragging) {
      const canvas = canvasRef.current;
      if (!canvas) return;

      if (!image) {
        canvas.style.cursor = "default";
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      let isHover = false;
      let hoverResource = null;

      resourceList.forEach((resource) => {
        const adjustedX =
          canvas.width / 2 + offset.x + resource.coordinateX * scale;
        const adjustedY =
          canvas.height / 2 + offset.y + resource.coordinateY * scale;
        const radius = spotSize * scale;

        const distance = Math.sqrt(
          Math.pow(clickX - adjustedX, 2) + Math.pow(clickY - adjustedY, 2)
        );
        if (distance < radius) {
          isHover = true;
          hoverResource = resource;
        }
      });

      setIsHover(isHover);
      canvas.style.cursor = isHover ? "pointer" : "grab";
      setHoverResource(hoverResource);
      drawCanvas();
    } else {
      const dx = e.clientX - lastMousePos.x;
      const dy = e.clientY - lastMousePos.y;
      setOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      setLastMousePos({ x: e.clientX, y: e.clientY });

      const canvas = canvasRef.current;
      if (!canvas) return;

      if (!image) {
        canvas.style.cursor = "default";
      } else {
        canvas.style.cursor = "grabbing";
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 원 클릭 시 해당 원의 좌표를 출력
  const handleCircleClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // 클릭된 원을 찾기 위해 캔버스 상에서 클릭 위치와 원의 위치 비교
    resourceList.forEach((resource) => {
      const adjustedX =
        canvas.width / 2 + offset.x + resource.coordinateX * scale;
      const adjustedY =
        canvas.height / 2 + offset.y + resource.coordinateY * scale;
      const distance = Math.sqrt(
        Math.pow(clickX - adjustedX, 2) + Math.pow(clickY - adjustedY, 2)
      );

      if (distance <= 10 * scale) {
        // 클릭한 원
        let isBookAvailable = true;

        bookList.forEach((book) => {
          if (book.resource.id === resource.id) {
            e.stopPropagation();
            switch (book.status) {
              case "AVAILABLE":
                onOpenBookModal(
                  book,
                  resource,
                  adjustedX - spotSize / 2,
                  adjustedY + spotSize / 2
                );
                isBookAvailable = false;
                break;
              case "IN_USE":
                onOpenBookModal(book, resource, clickX, clickY);
                isBookAvailable = false;
                break;
              case "NOT_AVAILABLE":
                isBookAvailable = false;
                break;
              default:
                break;
            }
          }
        });

        if (isBookAvailable) {
          onBook(resource);
        }
      }
    });
  };

  return (
    <section className={style.canvas_wrap}>
      <section
        className={style.canvas_container}
        ref={containerRef}
        style={{ width: "100%", height: "100%" }}
      >
        {isOpenModal && (
          <BookModal
            book={book}
            resource={resource}
            pointer={pointer}
            onClose={onCloseModal}
          />
        )}
        {isOpenResource && (
          <ResourceModal
            data={resource}
            pointer={pointer}
            onClose={onCloseResource}
          />
        )}
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={handleCircleClick}
        />

        <div className={style.button}>
          <button onClick={() => handleZoom(1.2)}>
            <ZoomInIcon />
          </button>
          <button onClick={() => handleZoom(0.8)}>
            <ZoomOutIcon />
          </button>
        </div>
      </section>
    </section>
  );
};

export default PlanCanvas;

function BookModal({ book, resource, pointer, onClose }) {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    window.addEventListener("mousedown", onClose);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("mousedown", onClose);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  const formatStatus = (status) => {
    switch (status) {
      case type.AVAILABLE.value:
        return t("HOTDESKING.SEAT_GUIDE.RESERVED");
      case type.NOT_AVAILABLE.value:
        return t("HOTDESKING.SEAT_GUIDE.NOT_AVAILABLE");
      case type.IN_USE.value:
        return t("HOTDESKING.SEAT_GUIDE.IN_USE");
      default:
        return t("HOTDESKING.SEAT_GUIDE.NOT_USE");
    }
  };
  const formatColor = (status) => {
    switch (status) {
      case type.AVAILABLE.value:
        return type.AVAILABLE.color;
      case type.NOT_AVAILABLE.value:
        return type.NOT_AVAILABLE.color;
      case type.IN_USE.value:
        return type.IN_USE.color;
      default:
        return type.NOT_USE.color;
    }
  };
  return (
    <div
      className={style.canvas_modal}
      style={{
        top: pointer.y - 150 / 2 - spotSize,
        left: pointer.x + spotSize * 2,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <section className={style.modal_header}>
        <p className={style.title}>{resource.title}</p>
        <div className={style.status}>
          <span
            className={style.indicator}
            style={{ backgroundColor: formatColor(book.status) }}
          />
          <p className={style.label}>{formatStatus(book.status)}</p>
        </div>
      </section>

      <section className={style.modal_user}>
        <Avatar
          className={style.user_img}
          icon={<UserOutlined className={style.icon} />}
        />
        <section className={style.info}>
          <p className={style.user_name}>{book.member.name}</p>
          <article className={style.user_department}>
            <p>{book.member.role}</p>
            <span className={style.driver} />
            <p>{book.member.department.name}</p>
          </article>
        </section>
      </section>

      <span className={style.driver} />

      <section className={style.modal_time}>
        <div className={style.item}>
          <div className={style.label}>
            <CalendarIcon />
            <p>{t("HOTDESKING.MODAL.BOOK_INFO.DATE")}</p>
          </div>
          <p className={style.value}>
            {formatDate(book.startDttm, i18n.language)}
          </p>
        </div>

        <div className={style.item}>
          <div className={style.label}>
            <TimeIcon />
            <p>{t("HOTDESKING.MODAL.BOOK_INFO.TIME")}</p>
          </div>
          <p className={style.value}>
            {formatTime(book.startDttm, book.endDttm)}(
            {formatDuration(book.duration, i18n.language)})
          </p>
        </div>
      </section>
    </div>
  );
}
function ResourceModal({ data, pointer, onClose }) {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    window.addEventListener("mousedown", onClose);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("mousedown", onClose);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);
  return (
    <div
      className={style.canvas_modal}
      style={{
        // top: pointer.y - 150 / 2 - spotSize,
        top: pointer.y - 70 / 2 - spotSize,
        left: pointer.x + spotSize * 2,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <section className={style.modal_header}>
        <p className={style.title}>{data?.resource.title}</p>
        <div className={style.status}>
          <span
            className={style.indicator}
            style={{ backgroundColor: type.NOT_USE.color }}
          />
          <p className={style.label}>{type.NOT_USE.label}</p>
        </div>
      </section>

      <span className={style.driver} />

      <section className={style.modal_time}>
        <div className={style.item}>
          <div className={style.label}>
            <CalendarIcon />
            <p>{t("HOTDESKING.MODAL.RESOURCE.DATE")}</p>
          </div>
          <p className={style.value}>{formatDate(data?.date, i18n.language)}</p>
        </div>

        <div className={style.item}>
          <div className={style.label}>
            <TimeIcon />
            <p>{t("HOTDESKING.MODAL.RESOURCE.DATE")}</p>
          </div>
          <p className={style.value}>
            {formatTime(
              data?.date + " " + data?.time.start,
              data?.date + " " + data?.time.end
            )}
          </p>
        </div>
      </section>
    </div>
  );
}
const formatDate = (date, language) => {
  if (language === "ko-KR") {
    return dayjs(new Date(date)).format("YYYY년 MM월 DD일 (ddd요일)");
  } else {
    return dayjs(new Date(date)).format("YYYY. MM. DD. (ddd)");
  }
};
const formatTime = (startDttm, endDttm) => {
  const formatStartDttm = dayjs(new Date(startDttm));
  const formatEndDttm = dayjs(new Date(endDttm));
  return (
    <>
      {formatStartDttm.format("HH:mm")} ~ {formatEndDttm.format("HH:mm")}
    </>
  );
};
const formatDuration = (duration, language) => {
  let hours = Math.floor(duration / 60);
  let mins = duration % 60;
  if (language === "ko-KR") {
    return `${String(hours).padStart(2, "0")}시간 ${String(mins).padStart(
      2,
      "0"
    )}분`;
  } else {
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  }
};
